import { createContext, createElement, useCallback, useContext, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { proxyFetch } from "./api/client.js";
import i18n from "./i18n.js";
import { readProxyLocale } from "./utils/proxyLocale.js";

const WithdrawalWizardContext = createContext(null);

const defaultValues = {
  customer_first_name: "",
  customer_last_name: "",
  customer_email: "",
  order_reference: "",
  language: readProxyLocale(),
  items_description: "",
  withdrawal_reason_id: "",
  reason: "",
  customer_phone: "",
  additional_notes: "",
  preferred_contact_method: "",
  line_item_selected: {},
  line_item_quantities: {},
  line_item_reason_ids: {},
  line_item_reason_details: {},
};

export function WithdrawalWizardProvider({ wizard, children }) {
  return createElement(WithdrawalWizardContext.Provider, { value: wizard }, children);
}

export function useWithdrawalWizardContext() {
  const wizard = useContext(WithdrawalWizardContext);
  if (!wizard) {
    throw new Error("useWithdrawalWizardContext must be used inside WithdrawalWizardProvider");
  }

  return wizard;
}

export function safeLineItemKey(id) {
  return String(id).replace(/[^a-zA-Z0-9_-]/g, "_");
}

export function lineItemId(item) {
  return String(item.shopify_line_item_id || item.shopifyLineItemId || item.id || "");
}

export function useWithdrawalWizard({ routes }) {
  const form = useForm({ defaultValues });
  const { getValues, reset } = form;
  const [step, setStep] = useState("customer");
  const [orderPayload, setOrderPayload] = useState(null);
  const [manualReasons, setManualReasons] = useState([]);
  const [confirmationWithdrawal, setConfirmationWithdrawal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const goToStep = useCallback((nextStep) => {
    setStep(nextStep);
  }, []);

  const goToCustomer = useCallback(() => {
    goToStep("customer");
  }, [goToStep]);

  const resetFlowFields = useCallback(
    (values = {}) => {
      reset({
        ...defaultValues,
        ...customerPayload(getValues()),
        ...values,
        language: readProxyLocale(),
      });
    },
    [getValues, reset],
  );

  const runLookup = useCallback(
    async (values) => {
      setLoading(true);
      setError(null);

      try {
        const { data } = await proxyFetch(routes.lookup, {
          method: "POST",
          json: { ...customerPayload(values), language: readProxyLocale() },
        });

        if (!data.ok) {
          setError(data.error || i18n.t("proxy.withdrawals.errors.unknown"));
          return;
        }

        resetFlowFields(data.customer || customerPayload(values));
        setConfirmationWithdrawal(null);

        if (data.next === "manual") {
          setManualReasons(data.withdrawal_reasons || []);
          setOrderPayload(null);
          setStep("manual");
        } else if (data.next === "excluded") {
          setManualReasons([]);
          setOrderPayload(null);
          setStep("excluded");
        } else if (data.next === "order") {
          setManualReasons([]);
          setOrderPayload(data);
          setStep("order");
        }
      } catch (requestError) {
        setError(requestError?.message || i18n.t("proxy.withdrawals.errors.unknown"));
      } finally {
        setLoading(false);
      }
    },
    [resetFlowFields, routes.lookup],
  );

  const runCreate = useCallback(
    async (body) => {
      setLoading(true);
      setError(null);

      try {
        const { data } = await proxyFetch(routes.create, {
          method: "POST",
          json: { ...body, language: readProxyLocale() },
        });

        if (data.ok && data.withdrawal) {
          setConfirmationWithdrawal(data.withdrawal);
          setOrderPayload(null);
          setManualReasons([]);
          setStep("confirmation");
          return;
        }

        setError(data.error || i18n.t("proxy.withdrawals.errors.create_failed"));
        if (data.step === "order") {
          setOrderPayload(data);
          setStep("order");
        } else {
          setManualReasons(data.withdrawal_reasons || []);
          setStep("manual");
        }
      } catch (requestError) {
        setError(requestError?.message || i18n.t("proxy.withdrawals.errors.create_failed"));
      } finally {
        setLoading(false);
      }
    },
    [routes.create],
  );

  const submitManual = useCallback(
    (values) => {
      const selectedReason = manualReasons.find((candidate) => String(candidate.id) === String(values.withdrawal_reason_id));

      return runCreate({
        ...customerPayload(values),
        items_description: values.items_description,
        withdrawal_reason_id: values.withdrawal_reason_id || undefined,
        reason: selectedReason?.key === "other" ? values.reason : undefined,
        ...optionalPayload(values),
      });
    },
    [manualReasons, runCreate],
  );

  const submitOrder = useCallback(
    (values) => {
      const lineItems = orderPayload?.line_items || [];
      const availabilityById = orderPayload?.line_item_availability || {};
      const selectedLineItemIds = lineItems
        .map(lineItemId)
        .filter((id) => values.line_item_selected?.[safeLineItemKey(id)] && availabilityById[id]?.available !== false);
      const lineItemQuantities = {};
      const lineItemReasonIds = {};
      const lineItemReasonDetails = {};

      selectedLineItemIds.forEach((id) => {
        const key = safeLineItemKey(id);
        const item = lineItems.find((candidate) => lineItemId(candidate) === id);
        const available = availabilityById[id]?.available_quantity ?? Number(item?.quantity || 1);
        lineItemQuantities[id] = Number(values.line_item_quantities?.[key] || available);
        if (values.line_item_reason_ids?.[key]) lineItemReasonIds[id] = values.line_item_reason_ids[key];
        if (values.line_item_reason_details?.[key]) lineItemReasonDetails[id] = values.line_item_reason_details[key];
      });

      return runCreate({
        ...customerPayload(values),
        signed_order_token: orderPayload?.signed_order_token,
        selected_line_item_ids: selectedLineItemIds,
        line_item_quantities: lineItemQuantities,
        line_item_reason_ids: lineItemReasonIds,
        line_item_reason_details: lineItemReasonDetails,
        ...optionalPayload(values),
      });
    },
    [orderPayload, runCreate],
  );

  return useMemo(
    () => ({
      form,
      step,
      orderPayload,
      manualReasons,
      confirmationWithdrawal,
      loading,
      error,
      goToStep,
      goToCustomer,
      runLookup,
      submitManual,
      submitOrder,
    }),
    [confirmationWithdrawal, error, form, goToCustomer, goToStep, loading, manualReasons, orderPayload, runLookup, step, submitManual, submitOrder],
  );
}

function customerPayload(values) {
  return {
    customer_first_name: values.customer_first_name || "",
    customer_last_name: values.customer_last_name || "",
    customer_email: values.customer_email || "",
    order_reference: values.order_reference || "",
  };
}

function optionalPayload(values) {
  return {
    customer_phone: values.customer_phone || "",
    additional_notes: values.additional_notes || "",
    preferred_contact_method: values.preferred_contact_method || "",
  };
}
